import React, { useState, useEffect } from "react";
import Sidebar from "../../components/Admin/Sidebar";
import Header from "../../components/Admin/Header";
import styles from "./Settings.module.css";
import api from "../../api/axois";
import { useUser } from "../../context/UserContext";

const Settings = () => {
  const { user, setUser } = useUser();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    profile_photo: "",
  });
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [isEditable, setIsEditable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  // WhatsApp / app settings state
  const [appSettings, setAppSettings] = useState({ whatsapp_from: "", support_phone: "" });
  const [waEditable, setWaEditable] = useState(false);
  const [waSaving, setWaSaving] = useState(false);
  const [waSuccess, setWaSuccess] = useState("");
  const [waError, setWaError] = useState("");

  useEffect(() => {
    // Fetch user info from context or API
    if (user) {
      setFormData({
        first_name: user && user.first_name ? user.first_name : "",
        last_name: user && user.last_name ? user.last_name : "",
        email: user && user.email ? user.email : "",
        password: "",
        profile_photo: user.profile_photo || "",
      });
      setLoading(false);
    } else {
      api.get("/user").then(res => {
        setFormData({
          first_name: res.data.first_name || "",
          last_name: res.data.last_name || "",
          email: res.data.email || "",
          password: "",
          profile_photo: res.data.profile_photo || "",
        });
        setUser(res.data);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [user, setUser]);

  // Load app-level settings (WhatsApp numbers)
  useEffect(() => {
    api.get("/admin/app-settings")
      .then(res => {
        const map = {};
        (res.data.settings || []).forEach(s => { map[s.key] = s.value || ""; });
        setAppSettings(prev => ({ ...prev, ...map }));
      })
      .catch(() => {});
  }, []);

  const handleWaSave = async () => {
    setWaSaving(true);
    setWaError("");
    setWaSuccess("");
    try {
      await api.put("/admin/app-settings", { settings: appSettings });
      setWaSuccess("WhatsApp settings saved.");
      setWaEditable(false);
    } catch (e) {
      setWaError(e.response?.data?.message || "Failed to save settings.");
    } finally {
      setWaSaving(false);
    }
  };

  const handleChange = (e) => {
    if (!isEditable) return;
    const { name, value, files } = e.target;
    if (name === "profile_photo" && files && files[0]) {
      setFormData({ ...formData, profile_photo: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSave = async () => {
    setError("");
    setSuccess("");
    setFieldErrors({});
    try {
      const data = new FormData();
      data.append("first_name", formData.first_name);
      data.append("last_name", formData.last_name);
      data.append("email", formData.email);
      if (formData.password) {
        data.append("password", formData.password);
        data.append("password_confirmation", passwordConfirm);
      }
      if (formData.profile_photo && typeof formData.profile_photo !== "string") {
        data.append("profile_photo", formData.profile_photo);
      }
      const res = await api.post("/user/update", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUser(res.data.user);
      setSuccess("Profile updated successfully!");
      setIsEditable(false);
      setPasswordConfirm("");
    } catch (err) {
      if (err.response?.data?.errors) {
        setFieldErrors(err.response.data.errors);
        setError("Please fix the errors below.");
      } else {
        setError(err.response?.data?.message || "Failed to update profile.");
      }
    }
  };

  const handleCancel = () => {
    setIsEditable(false);
    setError("");
    setSuccess("");
    // Reset form to user info
    if (user) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email: user.email || "",
        password: "",
        profile_photo: user.profile_photo || "",
      });
    }
  };

  return (
    <div className={styles.container}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={styles.content}>
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <div className={styles.wrapper}>
          <h2 className={styles.heading}>Admin Settings</h2>
          {loading ? (
            <div>Loading...</div>
          ) : (
            <>
              <div className={styles.profileSection}>
                <label className={styles.photoLabel}>Profile</label>
                <div className={styles.photoRow}>
                  <div
                    className={styles.profilePhoto}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#1976d2',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '1.5rem',
                      width: 60,
                      height: 60,
                      borderRadius: '100px',
                      userSelect: 'none',
                    }}
                  >
                    {`${(formData.first_name || '').charAt(0)}${(formData.last_name || '').charAt(0)}`.toUpperCase()}
                  </div>
                </div>
              </div>

              <div className={styles.form}>
                <div className={styles.formRow}>
                  <label className={styles.label}>First Name</label>
                  <input
                    type="text"
                    name="first_name"
                    className={styles.input}
                    value={formData.first_name}
                    onChange={handleChange}
                    disabled={!isEditable}
                  />
                </div>
                <div className={styles.formRow}>
                  <label className={styles.label}>Last Name</label>
                  <input
                    type="text"
                    name="last_name"
                    className={styles.input}
                    value={formData.last_name}
                    onChange={handleChange}
                    disabled={!isEditable}
                  />
                </div>
                <div className={styles.formRow}>
                  <label className={styles.label}>Email</label>
                  <input
                    type="email"
                    name="email"
                    className={styles.input}
                    value={formData.email}
                    onChange={handleChange}
                    disabled={!isEditable}
                  />
                </div>
                <div className={styles.formRow}>
                  <label className={styles.label}>Password</label>
                  <input
                    type="password"
                    name="password"
                    className={styles.input}
                    value={formData.password}
                    onChange={handleChange}
                    disabled={!isEditable}
                    placeholder="Leave blank to keep current password"
                  />
                </div>
                <div className={styles.formRow}>
                  <label className={styles.label}>Confirm Password</label>
                  <input
                    type="password"
                    name="password_confirmation"
                    className={styles.input}
                    value={passwordConfirm}
                    onChange={e => setPasswordConfirm(e.target.value)}
                    disabled={!isEditable}
                    placeholder="Re-enter new password"
                  />
                  {fieldErrors.password_confirmation && (
                    <div className={styles.error}>{fieldErrors.password_confirmation[0]}</div>
                  )}
                </div>
              </div>
              {error && <div className={styles.error}>{error}</div>}
              {success && <div className={styles.success}>{success}</div>}
              <div className={styles.actions}>
                <button
                  className={styles.saveButton}
                  onClick={handleSave}
                  disabled={!isEditable}
                >
                  Save
                </button>
                <button
                  className={styles.cancelButton}
                  onClick={handleCancel}
                  disabled={!isEditable}
                >
                  Cancel
                </button>
              </div>

              {/* ─── WhatsApp / Notification Settings ─────────────────────── */}
              <div style={{ marginTop: "2rem", borderTop: "1px solid #e5e7eb", paddingTop: "1.5rem" }}>
                <h3 style={{ marginBottom: "1rem", fontSize: "1.1rem", fontWeight: 600 }}>
                  📱 WhatsApp Notification Settings
                </h3>
                <p style={{ fontSize: 13, opacity: 0.65, marginBottom: "1rem" }}>
                  These numbers are used to send booking confirmation WhatsApp messages via Twilio.
                  The <strong>From Number</strong> must be a Twilio WhatsApp-enabled number.
                </p>

                <div className={styles.formRow}>
                  <label className={styles.label}>From Number (Aurelia's WhatsApp)</label>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="+2348012345678"
                    value={appSettings.whatsapp_from || ""}
                    onChange={e => setAppSettings(prev => ({ ...prev, whatsapp_from: e.target.value }))}
                    disabled={!waEditable}
                  />
                </div>
                <div className={styles.formRow}>
                  <label className={styles.label}>Support / Contact Phone</label>
                  <input
                    type="text"
                    className={styles.input}
                    placeholder="+2348012345678"
                    value={appSettings.support_phone || ""}
                    onChange={e => setAppSettings(prev => ({ ...prev, support_phone: e.target.value }))}
                    disabled={!waEditable}
                  />
                </div>

                {waError && <div className={styles.error}>{waError}</div>}
                {waSuccess && <div className={styles.success}>{waSuccess}</div>}

                <div className={styles.actions}>
                  {!waEditable ? (
                    <button className={styles.saveButton} onClick={() => { setWaEditable(true); setWaSuccess(""); setWaError(""); }}>
                      Edit
                    </button>
                  ) : (
                    <>
                      <button className={styles.saveButton} onClick={handleWaSave} disabled={waSaving}>
                        {waSaving ? "Saving…" : "Save"}
                      </button>
                      <button className={styles.cancelButton} onClick={() => setWaEditable(false)}>
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {sidebarOpen && (
        <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />
      )}
    </div>
  );
};

export default Settings;
